import React from 'react';
import { getBrokerDisplayName } from '../../utils/dataProcessing';

interface DashboardFiltersProps {
  brokers: string[];
  filters: {
    broker: string;
    year: number;
    yearType: 'calendar' | 'financial';
    activityStatus: 'all' | 'active' | 'inactive';
  };
  setFilters: React.Dispatch<React.SetStateAction<any>>;
  userRole: string;
  isOfficeDashboard: boolean;
  earliestYear?: number; // ✅ DYNAMIC: First year with commission data
  latestYear?: number; // ✅ DYNAMIC: Most recent year with data
}

const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  brokers,
  filters,
  setFilters,
  userRole,
  isOfficeDashboard,
  earliestYear,
  latestYear,
}) => {
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement> | { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    
    if (name === 'yearType') {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      let defaultYear: number;

      if (value === 'financial') {
        defaultYear = currentMonth >= 6 ? currentYear : currentYear - 1;
      } else {
        defaultYear = currentYear;
      }
      
      setFilters((prev: any) => ({ ...prev, yearType: value, year: defaultYear }));
    } else {
      // <select> always yields a string; year must stay a number (the callables'
      // strict year schema rejects "2025" with invalid-argument).
      setFilters((prev: any) => ({ ...prev, [name]: name === 'year' ? Number(value) : value }));
    }
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years: { value: number; label: string | number }[] = [];

    // Use dynamic range if available, otherwise default to 5 years
    // ✅ FIX (2026-01-01): Always include current year even if no data exists yet
    const dynamicLatestYear = Math.max(latestYear ?? currentYear, currentYear);
    const dynamicEarliestYear = earliestYear ?? (currentYear - 4);

    if (filters.yearType === 'financial') {
      const currentMonth = new Date().getMonth();
      // For financial years, start from current FY and go back to earliest data
      const startYear = currentMonth >= 6 ? dynamicLatestYear : dynamicLatestYear - 1;
      // Go back to earliest year that could have data in a financial year
      const endYear = dynamicEarliestYear;
      for (let year = startYear; year >= endYear; year--) {
        years.push({ value: year, label: `${year}/${year + 1}` });
      }
    } else {
      // Calendar years: from latest to earliest
      for (let year = dynamicLatestYear; year >= dynamicEarliestYear; year--) {
        years.push({ value: year, label: year });
      }
    }
    return years;
  };

  const yearOptions = generateYearOptions();
  
  const canSeeAdminFilters = isOfficeDashboard && ['super_admin', 'office_admin', 'administrator'].includes(userRole);

  return (
    <div className="bg-gray-800 p-4 rounded-lg shadow-md mb-6 flex flex-wrap items-end gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-400">Period Type</label>
        <div className="mt-1 flex gap-x-2 bg-gray-700 border-gray-600 rounded-md p-1">
          <button 
            onClick={() => handleFilterChange({ target: { name: 'yearType', value: 'calendar' } })}
            className={`px-3 py-1 text-sm rounded-md w-full ${filters.yearType === 'calendar' ? 'bg-blue-600 text-white' : 'text-gray-300'}`}
          >
            Calendar
          </button>
          <button 
            onClick={() => handleFilterChange({ target: { name: 'yearType', value: 'financial' } })}
            className={`px-3 py-1 text-sm rounded-md w-full ${filters.yearType === 'financial' ? 'bg-blue-600 text-white' : 'text-gray-300'}`}
          >
            Financial
          </button>
        </div>
      </div>

      {canSeeAdminFilters && (
        <div>
          <label className="block text-sm font-medium text-gray-400">Activity Status</label>
          <div className="mt-1 flex gap-x-2 bg-gray-700 border-gray-600 rounded-md p-1">
            <button 
              onClick={() => handleFilterChange({ target: { name: 'activityStatus', value: 'all' } })}
              className={`px-3 py-1 text-sm rounded-md ${filters.activityStatus === 'all' ? 'bg-blue-600 text-white' : 'text-gray-300'}`}
            >
              All
            </button>
            <button 
              onClick={() => handleFilterChange({ target: { name: 'activityStatus', value: 'active' } })}
              className={`px-3 py-1 text-sm rounded-md ${filters.activityStatus === 'active' ? 'bg-blue-600 text-white' : 'text-gray-300'}`}
            >
              Active
            </button>
            <button 
              onClick={() => handleFilterChange({ target: { name: 'activityStatus', value: 'inactive' } })}
              className={`px-3 py-1 text-sm rounded-md ${filters.activityStatus === 'inactive' ? 'bg-blue-600 text-white' : 'text-gray-300'}`}
            >
              Non-Active
            </button>
          </div>
        </div>
      )}
      
      {canSeeAdminFilters && (
        <div>
          <label className="block text-sm font-medium text-gray-400">Broker</label>
          <select 
            name="broker" 
            value={filters.broker} 
            onChange={handleFilterChange} 
            className="mt-1 block w-full bg-gray-700 border-gray-600 text-white rounded-md p-2"
          >
            <option value="">All Brokers</option>
            {brokers.map((b: any) => <option key={b} value={b}>{getBrokerDisplayName(b)}</option>)}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-400">Year</label>
        <select 
          name="year" 
          value={filters.year} 
          onChange={handleFilterChange} 
          className="mt-1 block w-full bg-gray-700 border-gray-600 text-white rounded-md p-2"
        >
          {yearOptions.map((y: any) => <option key={y.value} value={y.value}>{y.label}</option>)}
        </select>
      </div>
    </div>
  );
};

export default DashboardFilters;